import {
  AddPartnerModal,
  DeleteConfirmModal,
  IconImage,
  RestrictUserModal,
  UnblockUserModal,
  UpdatePartnerModal,
} from "@/components";
import { black_filter_img, search_img } from "@/constants";
import { useDebounce, useSearchQuery, useSnackbar } from "@/hooks";
import {
  useDeleteSpOwnerPartnerMutation,
  useGetAllSpOwnerPartnersQuery,
  usePostSpOwnerPartnerMutation,
  useUpdatePartnerStatusMutation,
  useUpdateSpOwnerPartnerMutation,
} from "@/services";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Skeleton,
} from "@mui/material";
import { MouseEvent, useState } from "react";
import { IoMdAdd, IoMdClose } from "react-icons/io";
import Filters from "./filters";
import {
  HeaderRightNode,
  StyledContainedButton,
  StyledTextInput,
} from "./styles";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { tokens } from "@/tokens";
import { tableCompactSx } from "@/components/table/table-styles";
import { formatDateOrTime } from "@/utils";
import { delete_img, unlock_right } from "@/constants";

const initialModalState = {
  type: "", // "delete", "restrict", "unblock"
  open: false,
  partnerName: "",
  _id: "",
  isRestricted: false,
  email: "",
};

const initalFilterState: Pick<UserFilter, "status"> = {
  status: "",
};

const SPOWNERPartners = () => {
  const { updateQuery, getQueryValue } = useSearchQuery();
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [filterState, setFilterState] = useState(initalFilterState);
  const [modalState, setModalState] = useState(initialModalState);
  const [showAddPartnerModal, setShowAddPartnerModal] = useState(false);
  const [showUpdatePartnerModal, setShowUpdatePartnerModal] = useState(false);
  const [deleteSpOwnerPartner, { isLoading: isDeletingUser }] =
    useDeleteSpOwnerPartnerMutation();
  const [postSpOwnerPartner, { isLoading: isCreatingPartner }] =
    usePostSpOwnerPartnerMutation();
  const [updateSpOwnerPartner, { isLoading: isUpdatingPartner }] =
    useUpdateSpOwnerPartnerMutation();

  const [updatePartnerStatus, { isLoading: partnerStatusLoading }] =
    useUpdatePartnerStatusMutation();

  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(
    null
  );
  const [partnerDetails, setPartnerDetails] = useState<any>({
    firstName: "",
    lastName: "",
    email: "",
    _id: "",
    accessibleClients: [] as string[],
  });
  const filterCount = Object.values(filterState).filter(Boolean).length;
  const navigate = useNavigate();
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const user = useAppSelector((state) => state.auth.user);
  const isTDAdminSP =
    user?.userRole === roles.TDADMIN &&
    selectedTenant?.tenantType === "SERVICE_PROVIDER";

  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetAllSpOwnerPartnersQuery(
    {
      limit,
      page: page + 1,
      searchKeyword: getQueryValue("search"),
      ...(filterState.status && {
        isRestricted: filterState.status !== "active" ? true : false,
      }),
      ...(isTDAdminSP && {
        tenantId: selectedTenant?._id,
      }),
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const { show } = useSnackbar();
  const { data = [], meta } = apiData || {};
  const totalCount = meta?.countDocs || 0;
  const { open, partnerName } = modalState;
  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === "object" && error !== null && "message" in error) {
      return String((error as { message: unknown }).message);
    }

    return String(error);
  };

  const handleTriggerModal = (item: SPOwnerPartnerTypes, type: string) => {
    if (type === "redirect") {
      navigate(`/clients?partner=${item?._id}`);
    } else {
      setModalState({
        type,
        open: true,
        partnerName: item.firstName + " " + item.lastName,
        _id: item?._id,
        email: item?.email,
        isRestricted: item.isRestricted,
      });
    }
  };

  const handleFilterChange = async (e: Pick<UserFilter, "status">) => {
    setFilterState(e);
  };

  const handleCloseModal = () => {
    setModalState(initialModalState);
  };

  const debouncedSearch = useDebounce((val: string) => {
    updateQuery({ search: val });
  }, 500);

  const handleOpenFilterPopover = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleRowClick = (partner: SPOwnerPartnerTypes) => {
    setPartnerDetails({
      firstName: partner.firstName,
      lastName: partner.lastName,
      email: partner.email,
      _id: partner._id,
      accessibleClients: (partner as any)?.accessibleClients || [],
      allowedPages: (partner as any)?.allowedPages || [],
    });
    setShowUpdatePartnerModal(true);
  };

  const handleDeletePartner = async () => {
    const userId = {
      userId: modalState?._id as string,
      ...(isTDAdminSP && { tenantId: selectedTenant?._id }),
    };

    try {
      await deleteSpOwnerPartner(userId).unwrap();
      handleCloseModal();
    } catch (error) {
      show(getErrorMessage(error), "", "error");
    }
  };

  const handleCreatePartner = async (data: SPOwnerPartnerTypes) => {
    const partnerData = {
      ...data,
      ...(isTDAdminSP && { tenantId: selectedTenant?._id }),
      ...(isTDAdminSP && {
        createdBy: selectedTenant?.users[0]._id
          ? selectedTenant?.users[0]._id
          : undefined,
      }),
    };
    try {
      await postSpOwnerPartner(partnerData).unwrap();
      setShowAddPartnerModal(false);
    } catch (error) {
      show(getErrorMessage(error), "", "error");
    }
  };

  const handleUpdatePartner = async (data: SPOwnerPartnerTypes) => {
    const { firstName, lastName } = data;
    try {
      await updateSpOwnerPartner({
        partnerData: { firstName, lastName },
        partnerId: partnerDetails?._id as string,
      }).unwrap();
      setShowUpdatePartnerModal(false);
    } catch (error) {
      show(getErrorMessage(error), "", "error");
    }
  };

  const handleUpdatePartnerStatus = async () => {
    try {
      await updatePartnerStatus({
        email: modalState.email,
        isRestricted: !modalState.isRestricted,
      }).unwrap();
      show(
        `Partner ${modalState.isRestricted ? "unblocked" : "restricted"}!`,
        "",
        "success"
      );
      setModalState(initialModalState);
    } catch (error: any) {
      show(error.message, "", "error");
    }
  };

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" width={200} /></TableCell>
      </TableRow>
    ));

  return (
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2, pt: 3, pb: 1.5, flexShrink: 0 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Typography fontWeight={tokens.fontWeight.semibold} variant="h5" flex={1} color={tokens.text.primary}>
            Partners
            {totalCount ? (
              <Box component="span" sx={{ color: tokens.text.muted, pl: 0.5 }}>
                ({totalCount})
              </Box>
            ) : null}
          </Typography>
          <HeaderRightNode gap={2}>
            <StyledTextInput
              type="text"
              placeholder="Search"
              handleChangeValue={debouncedSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconImage src={search_img} size="auto" />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ position: "relative" }}>
              {filterCount > 0 && (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "grid",
                    placeContent: "center",
                    backgroundColor: tokens.status.danger,
                    color: "white",
                    borderRadius: "50%",
                    position: "absolute",
                    top: "-10px",
                    right: "-9px",
                    zIndex: "1",
                  }}
                >
                  <Typography variant="caption" fontWeight={tokens.fontWeight.semibold} color="white">
                    {filterCount}
                  </Typography>
                </Box>
              )}
              <StyledContainedButton
                size="large"
                title="Filter"
                variant="outlined"
                style={{ borderColor: tokens.border.default, height: "34px" }}
                startIcon={<IconImage src={black_filter_img} size="auto" />}
                onClick={handleOpenFilterPopover}
              />
            </Box>
            <StyledContainedButton
              startIcon={<IoMdAdd />}
              size="large"
              title="Add Partner"
              onClick={() => {
                setShowAddPartnerModal(true);
                setPartnerDetails({
                  firstName: "",
                  lastName: "",
                  email: "",
                  _id: "",
                });
              }}
            />
          </HeaderRightNode>
        </Stack>
      </Box>

      {/* Table */}
      <Box sx={{ flex: 1, overflow: 'hidden', px: 2, pb: 2 }}>
        <Box sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.surface,
          ...tableCompactSx,
        }}>
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Partner Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Date Added</TableCell>
                  <TableCell align="center">Total Clients</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading || isFetching ? renderSkeletonRows() : (
                  <>
                    {(data as SPOwnerPartnerTypes[]).map((item) => (
                      <TableRow
                        key={item._id}
                        onClick={() => handleRowClick(item)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: tokens.bg.hover },
                          '& > td': { borderBottom: `1px solid ${tokens.border.light}` },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                            {item.firstName + " " + item.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                            {item.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                            {formatDateOrTime(item.createdAt, "date")}
                          </Typography>
                          <Typography variant="caption" sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.xs }}>
                            {formatDateOrTime(item.createdAt, "time")}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                            {item.totalClients ?? 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {!item.isRestricted ? (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<IoMdClose />}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleTriggerModal(item, "restrict");
                                }}
                                sx={{
                                  color: "rgba(234, 67, 52, 1)",
                                  borderColor: tokens.border.default,
                                  fontWeight: tokens.fontWeight.semibold,
                                  fontSize: tokens.fontSize.xs,
                                  textTransform: "none",
                                }}
                              >
                                Restrict
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<IconImage src={unlock_right} size="auto" />}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleTriggerModal(item, "unblock");
                                }}
                                sx={{
                                  color: "rgba(52, 168, 83, 1)",
                                  borderColor: tokens.border.default,
                                  fontWeight: tokens.fontWeight.semibold,
                                  fontSize: tokens.fontSize.xs,
                                  textTransform: "none",
                                }}
                              >
                                Unblock
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleTriggerModal(item, "delete");
                              }}
                              sx={{
                                color: tokens.status.danger,
                                borderColor: tokens.border.default,
                                minWidth: 'auto',
                                px: 1,
                              }}
                            >
                              <IconImage src={delete_img} size="auto" />
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleTriggerModal(item, "redirect");
                              }}
                              sx={{
                                color: tokens.text.primary,
                                borderColor: tokens.border.default,
                                fontWeight: tokens.fontWeight.semibold,
                                fontSize: tokens.fontSize.xs,
                                textTransform: "none",
                              }}
                            >
                              View Clients
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.length === 0 && !isLoading && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                            No partners found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalCount > 0 && (
            <Box sx={{ borderTop: `1px solid ${tokens.border.light}`, flexShrink: 0 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                  '.MuiTablePagination-toolbar': { minHeight: 40 },
                  '.MuiTablePagination-displayedRows': { fontSize: tokens.fontSize.base, color: tokens.text.secondary },
                  '.MuiTablePagination-selectLabel': { fontSize: tokens.fontSize.base, color: tokens.text.secondary },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Delete confirmation Modal */}
      {modalState.type === "delete" && (
        <DeleteConfirmModal
          open={open}
          itemName={`Partner "${partnerName}"`}
          subText="partner"
          onConfirm={handleDeletePartner}
          maxWidth="xs"
          onClose={handleCloseModal}
          btnLoader={isDeletingUser}
        />
      )}
      {/* Restrict User Modal */}
      {modalState.type === "restrict" && (
        <RestrictUserModal
          open={open}
          name={partnerName}
          onConfirm={handleUpdatePartnerStatus}
          onClose={handleCloseModal}
          loading={partnerStatusLoading}
        />
      )}
      <Filters
        anchorEl={anchorElement}
        onFiltersChange={handleFilterChange}
        initialFilterState={initalFilterState}
        onClose={() => setAnchorElement(null)}
      />
      {/* Unblock User Modal */}
      {modalState.type === "unblock" && (
        <UnblockUserModal
          open={open}
          name={partnerName}
          onConfirm={handleUpdatePartnerStatus}
          onClose={handleCloseModal}
          loading={partnerStatusLoading}
        />
      )}
      {/* Add Partner Modal */}
      {showAddPartnerModal && (
        <AddPartnerModal
          handleCreatePartner={handleCreatePartner}
          show={showAddPartnerModal}
          setShow={setShowAddPartnerModal}
          btnLoader={isCreatingPartner}
        />
      )}
      {/* Update Partner Modal */}
      {showUpdatePartnerModal && (
        <UpdatePartnerModal
          show={showUpdatePartnerModal}
          setShow={setShowUpdatePartnerModal}
          partnerDetails={partnerDetails}
          handleUpdatePartner={handleUpdatePartner}
          btnLoader={isUpdatingPartner}
        />
      )}
    </Stack>
  );
};

export default SPOWNERPartners;
